import { api } from './api';
import type {
  Catalogue,
  CatalogueProduct,
  CatalogueCategory,
  CatalogueScopeRules,
  SyncDiff,
  OptimizeRun,
  ProductWording,
} from '@traaker/shared';

export interface DiscoveryResult {
  sitemapUrls: string[];
  wafBlocked: boolean;
  source: 'robots' | 'conventional' | 'homepage-link' | 'serper' | 'none';
}

export interface CreateResult {
  catalogue: Catalogue;
  truncated: boolean;
}

/** A search match from the scope picker: n=name, c=categoryPath, t=type. */
export interface CatalogueSearchItem {
  n: string;
  c: string;
  t: 'p' | 'c';
}

export interface CataloguePreview {
  previewId: string;
  totalItems: number;
  totalProducts: number;
  totalCategories: number;
  sampleItems: Array<{ name: string; url: string; type: string; categoryPath: string }>;
  topCategoryPaths: Array<{ path: string; count: number }>;
  truncated: boolean;
}

export interface PreviewSearchResult {
  rows: CatalogueSearchItem[];
  total: number;
}

export interface RecountResult {
  matched: number;
  matchedProducts: number;
  matchedCategories: number;
}

export interface ItemsPage {
  items: Array<CatalogueProduct | CatalogueCategory>;
  nextCursor?: string;
}

/** Product module — Catalog page API (retailer-only). Base path is brand-scoped. */
export const retailerCatalogApi = {
  discover: (brandId: string, domain: string): Promise<DiscoveryResult> =>
    api.post<DiscoveryResult>(`/brands/${brandId}/catalogues/discover`, { domain }),

  list: (brandId: string): Promise<Catalogue[]> =>
    api.get<Catalogue[]>(`/brands/${brandId}/catalogues`),

  /** Step 1 — parse-first preview (harvest + cache, no persist). `itemType` forces
   *  the product/category verdict on every sitemap URL, when auto-detection got it
   *  wrong (id-less product URLs the shape heuristic can't recognize). */
  preview: (
    brandId: string,
    body: {
      source: 'sitemap' | 'upload';
      sitemapUrls?: string[];
      csv?: string;
      itemType?: 'product' | 'category';
    },
  ): Promise<CataloguePreview> =>
    api.post<CataloguePreview>(`/brands/${brandId}/catalogues/preview`, body),

  /** Step 2 — live recount of a scope against a cached preview. */
  recount: (
    brandId: string,
    body: { previewId: string; scopeRules: CatalogueScopeRules },
  ): Promise<RecountResult> =>
    api.post<RecountResult>(`/brands/${brandId}/catalogues/recount`, body),

  /** Step 2b — keyword-search the cached preview candidate set (scope picker). */
  searchPreview: (
    brandId: string,
    body: { previewId: string; q: string; limit?: number },
  ): Promise<PreviewSearchResult> =>
    api.post<PreviewSearchResult>(`/brands/${brandId}/catalogues/preview/search`, body),

  /** Step 2c — include-globs for ALL matches of a keyword ("Tout ajouter"). */
  matchGlobs: (
    brandId: string,
    body: { previewId: string; q: string },
  ): Promise<{ globs: string[]; total: number; capped: boolean }> =>
    api.post<{ globs: string[]; total: number; capped: boolean }>(
      `/brands/${brandId}/catalogues/preview/match-globs`,
      body,
    ),

  /** Step 3 — create from a cached preview (slug derived server-side). */
  create: (
    brandId: string,
    body: {
      name: string;
      description?: string;
      icon?: string;
      domain: string;
      language: string;
      previewId: string;
      source: 'sitemap' | 'upload';
      sitemapUrls?: string[];
      wafBlocked?: boolean;
      scopeRules?: CatalogueScopeRules;
      /** Must repeat the override the preview was parsed with (persisted for refresh). */
      itemType?: 'product' | 'category';
    },
  ): Promise<CreateResult> => api.post<CreateResult>(`/brands/${brandId}/catalogues`, body),

  getMeta: (brandId: string, catalogueId: string): Promise<Catalogue> =>
    api.get<Catalogue>(`/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}`),

  /** Edit cosmetic/descriptive metadata (name / description / icon). */
  updateMeta: (
    brandId: string,
    catalogueId: string,
    body: { name?: string; description?: string; icon?: string },
  ): Promise<Catalogue> =>
    api.patch<Catalogue>(`/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}`, body),

  items: (
    brandId: string,
    catalogueId: string,
    opts: { type?: 'product' | 'category'; categoryPath?: string; cursor?: string; limit?: number } = {},
  ): Promise<ItemsPage> => {
    const qs = new URLSearchParams();
    if (opts.type) qs.set('type', opts.type);
    if (opts.categoryPath) qs.set('categoryPath', opts.categoryPath);
    if (opts.cursor) qs.set('cursor', opts.cursor);
    if (opts.limit) qs.set('limit', String(opts.limit));
    const q = qs.toString();
    return api.get<ItemsPage>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/items${q ? `?${q}` : ''}`,
    );
  },

  previewRefresh: (brandId: string, catalogueId: string): Promise<SyncDiff> =>
    api.post<SyncDiff>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/refresh/preview`,
      {},
    ),

  applyRefresh: (brandId: string, catalogueId: string, syncDate: string): Promise<Catalogue> =>
    api.post<Catalogue>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/refresh/apply`,
      { syncDate },
    ),

  remove: (brandId: string, catalogueId: string): Promise<{ ok: boolean }> =>
    api.delete(`/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}`),

  /** Similarity search over a catalogue's vectors (Phase 3/6 grounding). Pass
   *  `hydrate` to also get a reader-friendly `displayName` per hit (for content
   *  link anchors — costs a BatchGet server-side, so opt-in). */
  search: (
    brandId: string,
    catalogueId: string,
    q: string,
    type?: 'product' | 'category',
    topK?: number,
    hydrate?: boolean,
  ): Promise<Array<{ productId: string; url: string; name: string; score: number; displayName?: string }>> => {
    const qs = new URLSearchParams({ q });
    if (type) qs.set('type', type);
    if (topK) qs.set('topK', String(topK));
    if (hydrate) qs.set('hydrate', '1');
    return api.get(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/search?${qs}`,
    );
  },

  /** Strategic product-change recommendations from competitive analysis (Phase 5). */
  strategicRecs: (
    brandId: string,
    catalogueId: string,
  ): Promise<Array<{ productId: string; title: string; detail: string; createdAt: string }>> =>
    api.get(`/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/strategic-recs`),

  /** Configure the catalogue's user-defined wording sections + which fixed
   *  sections are disabled (item b) — "Configurer" dialog. */
  updateCustomSections: (
    brandId: string,
    catalogueId: string,
    sections: import('@traaker/shared').CatalogueCustomSection[],
    disabledSections?: string[],
  ): Promise<Catalogue> =>
    api.put<Catalogue>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/custom-sections`,
      { sections, disabledSections },
    ),

  /** Configure the measurement match signals (which prove a recommendation). */
  updateMatchConfig: (
    brandId: string,
    catalogueId: string,
    body: { signals: string[]; brandTerms?: string[] },
  ): Promise<Catalogue> =>
    api.put<Catalogue>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/match-config`,
      body,
    ),

  // ---- optimization (Phase 2) ----
  startOptimize: (
    brandId: string,
    catalogueId: string,
    body: { onlyProducts?: boolean; onlyCategories?: boolean; onlyUnscored?: boolean } = {},
  ): Promise<OptimizeRun> =>
    api.post<OptimizeRun>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/optimize`,
      body,
    ),

  /** Worst-first paginated browse of optimize items (band/category filter) OR
   *  semantic search (`q`). Scales to a 50k catalogue. */
  optimizeItems: (
    brandId: string,
    catalogueId: string,
    opts: {
      type?: 'product' | 'category';
      band?: 'all' | 'critical' | 'improve' | 'good' | 'unscored';
      categoryPrefix?: string;
      q?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<ItemsPage> => {
    const qs = new URLSearchParams();
    if (opts.type) qs.set('type', opts.type);
    if (opts.band) qs.set('band', opts.band);
    if (opts.categoryPrefix) qs.set('categoryPrefix', opts.categoryPrefix);
    if (opts.q) qs.set('q', opts.q);
    if (opts.cursor) qs.set('cursor', opts.cursor);
    if (opts.limit) qs.set('limit', String(opts.limit));
    const s = qs.toString();
    return api.get<ItemsPage>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/optimize/items${s ? `?${s}` : ''}`,
    );
  },

  /** Score-band distribution (critical/improve/good + scored/total) for the header. */
  optimizeStats: (
    brandId: string,
    catalogueId: string,
    type: 'product' | 'category' = 'product',
  ): Promise<{ critical: number; improve: number; good: number; scored: number; total: number }> =>
    api.get(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/optimize/stats?type=${type}`,
    ),

  optimizeProgress: (
    brandId: string,
    catalogueId: string,
    runId?: string,
  ): Promise<OptimizeRun | null> =>
    api.get<OptimizeRun | null>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/optimize/progress${
        runId ? `?runId=${encodeURIComponent(runId)}` : ''
      }`,
    ),

  optimizeBlocked: (
    brandId: string,
    catalogueId: string,
  ): Promise<{ count: number; urls: string[] }> =>
    api.get<{ count: number; urls: string[] }>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/optimize/blocked`,
    ),

  updateWording: (
    brandId: string,
    catalogueId: string,
    sk: string,
    wording: ProductWording,
  ): Promise<{ ok: boolean }> =>
    api.post(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/items/wording`,
      { sk, wording },
    ),

  /**
   * Download the optimized-wording CSV. The API generates it to S3 (streamed,
   * so it scales to a 50k-item catalogue without a 6MB response cap) and
   * returns a short-lived presigned URL — no auth header needed, no blob.
   */
  downloadCsv: async (brandId: string, catalogueId: string): Promise<void> => {
    const { url } = await api.get<{ url: string }>(
      `/brands/${brandId}/catalogues/${encodeURIComponent(catalogueId)}/export.csv`,
    );
    window.location.href = url;
  },
};
